import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Linking,
  Platform,
  Image,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  MEANDER_APP_STORE_URL,
  MEANDER_PLAY_STORE_URL,
} from '../constants/app-store-links';

interface DownloadAppGateProps {
  title?: string;
  message?: string;
}

const DownloadAppGate: React.FC<DownloadAppGateProps> = ({
  title = 'Hosting requires the mobile app',
  message = 'To become a Meander host, please continue on the iOS or Android app. The mobile app is required to verify your account and register your charger.',
}) => {
  const openLink = (url: string) => {
    Linking.openURL(url).catch(() => {
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.iconWrap}>
        <MaterialCommunityIcons
          name="cellphone-arrow-down"
          size={56}
          color="#39BEB7"
        />
      </View>

      <Text style={styles.title}>{title}</Text>
      <Text style={styles.message}>{message}</Text>

      <View style={styles.buttonRow}>
        <Pressable
          onPress={() => openLink(MEANDER_APP_STORE_URL)}
          style={({ pressed }) => [
            styles.storeButton,
            pressed && styles.storeButtonPressed,
          ]}
          accessibilityRole="button"
          accessibilityLabel="Download Meander on the App Store"
        >
          <MaterialCommunityIcons name="apple" size={22} color="#FFFFFF" />
          <View style={styles.storeButtonTextWrap}>
            <Text style={styles.storeButtonSmall}>Download on the</Text>
            <Text style={styles.storeButtonLarge}>App Store</Text>
          </View>
        </Pressable>

        <Pressable
          onPress={() => openLink(MEANDER_PLAY_STORE_URL)}
          style={({ pressed }) => [
            styles.storeButton,
            pressed && styles.storeButtonPressed,
          ]}
          accessibilityRole="button"
          accessibilityLabel="Get Meander on Google Play"
        >
          <MaterialCommunityIcons
            name="google-play"
            size={22}
            color="#FFFFFF"
          />
          <View style={styles.storeButtonTextWrap}>
            <Text style={styles.storeButtonSmall}>Get it on</Text>
            <Text style={styles.storeButtonLarge}>Google Play</Text>
          </View>
        </Pressable>
      </View>

      <Text style={styles.helperText}>
        Already have the app? Open Meander on your phone and tap{' '}
        <Text style={styles.helperTextEmphasis}>Become a Host</Text> from My
        Account.
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  iconWrap: {
    width: 96,
    height: 96,
    borderRadius: 48,
    backgroundColor: 'rgba(57, 190, 183, 0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  message: {
    fontSize: 15,
    lineHeight: 22,
    color: '#C7CACF',
    textAlign: 'center',
    maxWidth: 480,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  storeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#1A1D21',
    borderWidth: 1,
    borderColor: '#39BEB7',
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 12,
    minWidth: 170,
  },
  storeButtonPressed: {
    opacity: 0.7,
  },
  storeButtonTextWrap: {
    flexDirection: 'column',
  },
  storeButtonSmall: {
    color: '#C7CACF',
    fontSize: 10,
  },
  storeButtonLarge: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '700',
  },
  helperText: {
    marginTop: 12,
    color: '#8D8E90',
    fontSize: 13,
    textAlign: 'center',
    maxWidth: 480,
  },
  helperTextEmphasis: {
    color: '#39BEB7',
    fontWeight: '600',
  },
});

export default DownloadAppGate;
